/**
 * Certification records: create, read, and record a retake.
 *
 * ── Ownership is decided here, not by the caller ──────────────────────────────────────────
 *
 * Every write fixes `employeeEmail` from the SIGNED-IN session unless the caller both asked for
 * someone else and is allowed to. A normal employee cannot create a certification for anybody
 * but themselves, and the way that is guaranteed is that the form has no say in it — passing an
 * email is ignored without the permission, rather than trusted and checked later.
 *
 * The server enforces the same boundary through the resolver's identity claims. This is the
 * near half of the same rule: hiding a button is not authorisation, and neither is this, but a
 * client that cannot express the wrong request is one fewer way to make it.
 *
 * ── The retake, which is the whole reason this is not plain CRUD ──────────────────────────
 *
 * `recordRetake` never overwrites a failed attempt. It appends to `attempts` and moves the
 * record's own fields to the new result. What the requirement calls "history must be preserved"
 * is that append: the original date, the original document and the original deadline all survive
 * on their attempt, and the certification reads Passed afterwards.
 */

import { generateClient } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import {
  Certification,
  CertificationAttempt,
  CertificationDraft,
  CertificationResult,
  CertificationSource,
  CertAdminDecision,
  calculateRetakeDeadline,
  validateCertificationDraft,
} from '../types/certification';
import { canonicalizeEmail } from './identity';

let client: any = null;
const getClient = () => {
  if (!client) client = generateClient();
  return client;
};

const authMode = { authMode: 'userPool' as const };
const getData = (result: any): any => ('data' in result ? result.data : null);

const FIELDS = `
  id
  employeeEmail
  employeeName
  employeeId
  name
  provider
  result
  examDate
  resultDate
  retakeDeadline
  completionDate
  expiryDate
  documentKey
  documentName
  attempts
  source
  expenseId
  certAdminDecision
  certAdminBy
  certAdminAt
  certAdminNote
  createdAt
  updatedAt
`;

const listQuery = /* GraphQL */ `
  query ListCertifications($limit: Int, $nextToken: String) {
    listCertifications(limit: $limit, nextToken: $nextToken) {
      items { ${FIELDS} }
      nextToken
    }
  }
`;

const byEmployeeQuery = /* GraphQL */ `
  query CertificationsByEmployee($employeeEmail: String!, $limit: Int, $nextToken: String) {
    certificationsByEmployee(employeeEmail: $employeeEmail, limit: $limit, nextToken: $nextToken) {
      items { ${FIELDS} }
      nextToken
    }
  }
`;

const createMutation = /* GraphQL */ `
  mutation CreateCertification($input: CreateCertificationInput!) {
    createCertification(input: $input) { ${FIELDS} }
  }
`;

const updateMutation = /* GraphQL */ `
  mutation UpdateCertification($input: UpdateCertificationInput!) {
    updateCertification(input: $input) { ${FIELDS} }
  }
`;

/**
 * `attempts` crosses the wire as an AWSJSON string.
 *
 * Stored as JSON rather than as a GraphQL list of a typed object because an attempt is an
 * append-only historical blob — nothing queries inside it, and a typed list would mean a second
 * input type, a second set of nullability rules, and stored rows nulled out by AppSync the first
 * time an older attempt lacked a field a newer schema made required.
 */
const parseAttempts = (raw: unknown): CertificationAttempt[] => {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // A record whose history cannot be read still has a current result worth showing. Losing the
    // history is bad; hiding the certification entirely is worse.
    console.warn('[Certifications] unreadable attempt history on a record; showing current only');
    return [];
  }
};

const fromRow = (row: any): Certification => ({
  ...row,
  employeeEmail: canonicalizeEmail(row?.employeeEmail),
  attempts: parseAttempts(row?.attempts),
});

/**
 * Turn a GraphQL failure into a sentence that names the actual cause.
 *
 * ── Why this exists ───────────────────────────────────────────────────────────────────────
 *
 * The most likely failure by far, for a while, is that the certification schema has not been
 * deployed to this environment yet — the table, the data source and the four resolvers all ship
 * through Terraform, and until that apply runs the API genuinely has no `listCertifications`.
 *
 * AppSync reports that as a field-validation error, and Amplify surfaces it as an ordinary
 * throw. Rendered as "Could not load certifications" it looks like a data problem or an outage,
 * and somebody goes hunting through logs for rows that were never going to be there. Naming it
 * turns a dead end into an instruction.
 *
 * ── Why it does NOT say "run the infrastructure workflow" ─────────────────────────────────
 *
 * It used to, and that became false. The module is now gated OFF behind
 * `certifications_enabled` in the dev environment, because a service control policy denies
 * dynamodb:CreateTable and an ungated apply died part way through, taking unrelated changes with
 * it. So running the pipeline today succeeds and changes nothing here — the advice would send
 * somebody to a green build and an unchanged page, which is worse than no advice.
 *
 * The message names the state instead of prescribing a fix, because the two causes need
 * different ones and the page cannot tell them apart from a null.
 */
export const CERTIFICATIONS_NOT_DEPLOYED =
  'The certifications API is not available in this environment. The DynamoDB tables and the ' +
  'AppSync resolvers ship through Terraform, and the module is currently switched off while an ' +
  'organisation policy blocks its tables — so nothing can be recorded or claimed here yet. ' +
  'Ask your administrator rather than retrying.';

/**
 * Thrown when the schema knows the field but nothing answers it.
 *
 * AppSync returns `null` for a field that has no resolver attached rather than raising an error,
 * and the certification resolvers are gated behind `certifications_enabled` while an
 * organisation policy blocks the table. Without this the page would read that null as "no rows"
 * and print "No certifications yet" — a confident, wrong answer that nobody would think to
 * question. A missing resolver is a deployment fact, so it is reported as one.
 */
export class CertificationsNotDeployedError extends Error {
  constructor() {
    super(CERTIFICATIONS_NOT_DEPLOYED);
    this.name = 'CertificationsNotDeployedError';
  }
}

export function describeCertificationError(error: any, fallback: string): string {
  if (error instanceof CertificationsNotDeployedError) return error.message;
  const messages: string[] = [];
  if (Array.isArray(error?.errors)) {
    for (const entry of error.errors) if (entry?.message) messages.push(String(entry.message));
  }
  if (error?.message) messages.push(String(error.message));
  const joined = messages.join(' ');

  /* "Validation error of type FieldUndefined: Field 'listCertifications' in type 'Query' is
     undefined" — the exact shape AppSync returns for a schema that predates this module. */
  if (/FieldUndefined|is undefined|Unknown type|Cannot query field/i.test(joined)) {
    return CERTIFICATIONS_NOT_DEPLOYED;
  }

  if (/Unauthorized|Not Authorized|AccessDenied/i.test(joined)) {
    return 'You are not authorised to read these certifications.';
  }

  return joined || fallback;
}

export class CertificationService {
  /**
   * Every certification, for a viewer allowed to see the organisation's.
   *
   * Paged rather than fetched whole: the list grows with headcount times certifications held,
   * and the page cap is what keeps a directory-wide read from becoming an unbounded scan.
   */
  static async listAll(limit = 200): Promise<Certification[]> {
    const items: Certification[] = [];
    let nextToken: string | undefined;
    let pages = 0;
    do {
      const result: any = await getClient().graphql({
        query: listQuery,
        variables: { limit, nextToken },
        ...authMode,
      });
      const page = getData(result)?.listCertifications;
      /* Null means no resolver is attached, not an empty table — see the error class. Only the
         FIRST page is checked: a null continuation would mean something else entirely. */
      if (!page && pages === 0) throw new CertificationsNotDeployedError();
      items.push(...(page?.items || []).filter(Boolean).map(fromRow));
      nextToken = page?.nextToken || undefined;
      pages += 1;
    } while (nextToken && pages < 25);
    return items;
  }

  /** One employee's certifications. The query an ordinary employee's page uses. */
  static async listForEmployee(employeeEmail: string): Promise<Certification[]> {
    const email = canonicalizeEmail(employeeEmail);
    if (!email) return [];
    const result: any = await getClient().graphql({
      query: byEmployeeQuery,
      variables: { employeeEmail: email, limit: 200 },
      ...authMode,
    });
    const page = getData(result)?.certificationsByEmployee;
    if (!page) throw new CertificationsNotDeployedError();
    return (page.items || []).filter(Boolean).map(fromRow);
  }

  /**
   * Add a certification record.
   *
   * `owner` is the session's employee. `draft.employeeEmail` is honoured ONLY when
   * `canRecordForOthers` — see the header. Everyone else records against themselves whatever
   * they send.
   */
  static async create(
    draft: CertificationDraft,
    owner: { email: string; name?: string; employeeId?: string },
    options: { canRecordForOthers?: boolean; source?: CertificationSource; expenseId?: string } = {}
  ): Promise<Certification> {
    const problems = validateCertificationDraft(draft);
    if (problems.length > 0) throw new Error(problems.join(' '));

    const employeeEmail = canonicalizeEmail(
      options.canRecordForOthers && draft.employeeEmail ? draft.employeeEmail : owner.email
    );
    if (!employeeEmail) throw new Error('A certification needs an employee.');

    const now = new Date().toISOString();
    const failed = draft.result === CertificationResult.FAILED;
    /* Calculated from the RESULT date, which is the date the requirement names and the one that
       exists on every record — examDate is optional. */
    const retakeDeadline = failed ? calculateRetakeDeadline(draft.resultDate) : undefined;

    const attempt: CertificationAttempt = {
      id: uuidv4(),
      result: draft.result,
      examDate: draft.examDate,
      resultDate: draft.resultDate,
      documentKey: draft.documentKey!,
      documentName: draft.documentName,
      retakeDeadline,
      recordedAt: now,
      recordedBy: canonicalizeEmail(owner.email),
    };

    const input = {
      employeeEmail,
      employeeName: owner.email === employeeEmail ? owner.name : undefined,
      employeeId: owner.email === employeeEmail ? owner.employeeId : undefined,
      name: draft.name.trim(),
      provider: draft.provider.trim(),
      result: draft.result,
      examDate: draft.examDate || undefined,
      resultDate: draft.resultDate,
      retakeDeadline,
      completionDate: failed ? undefined : draft.resultDate,
      // Only a pass can expire — a failed attempt has nothing to lapse.
      expiryDate: failed ? undefined : draft.expiryDate || undefined,
      documentKey: draft.documentKey,
      documentName: draft.documentName,
      attempts: JSON.stringify([attempt]),
      source: options.source || CertificationSource.DIRECT,
      expenseId: options.expenseId,
      /* A reimbursement claim enters the Certification Admin queue the moment it exists; a
         directly recorded certification has no gate at all, and leaving the field absent is how
         the queue tells the two apart. */
      certAdminDecision:
        options.source === CertificationSource.REIMBURSEMENT
          ? CertAdminDecision.PENDING
          : undefined,
    };

    const result: any = await getClient().graphql({
      query: createMutation,
      variables: { input },
      ...authMode,
    });
    /* Same null-means-no-resolver rule the read paths apply. Without it fromRow(null) returns an
       object with no id, the caller writes a claim pointing at nothing, and the failure surfaces
       three steps later as "the claim is not linked to a certification" — which blames the
       employee's input for a deployment fact. */
    const row = getData(result)?.createCertification;
    if (!row) throw new CertificationsNotDeployedError();
    return fromRow(row);
  }

  /**
   * Record a later sitting of the same exam, keeping the earlier one.
   *
   * ── What is appended and what moves ───────────────────────────────────────────────────
   *
   * The new attempt is APPENDED to `attempts`; nothing already in that list is touched. The
   * record's own `result`, `resultDate`, `documentKey` and `completionDate` move to the new
   * sitting, because those describe where the employee stands now.
   *
   * `retakeDeadline` is cleared on a pass and recalculated on another failure. Leaving a stale
   * deadline on a passed certification would show "retake required" beside a certificate the
   * employee has already earned.
   */
  static async recordRetake(
    certification: Certification,
    attemptDraft: Pick<
      CertificationDraft,
      'result' | 'examDate' | 'resultDate' | 'expiryDate' | 'documentKey' | 'documentName'
    >,
    actorEmail: string
  ): Promise<Certification> {
    const problems = validateCertificationDraft({
      name: certification.name,
      provider: certification.provider,
      ...attemptDraft,
    });
    if (problems.length > 0) throw new Error(problems.join(' '));

    const failed = attemptDraft.result === CertificationResult.FAILED;
    const retakeDeadline = failed ? calculateRetakeDeadline(attemptDraft.resultDate) : undefined;

    const attempt: CertificationAttempt = {
      id: uuidv4(),
      result: attemptDraft.result,
      examDate: attemptDraft.examDate,
      resultDate: attemptDraft.resultDate,
      documentKey: attemptDraft.documentKey!,
      documentName: attemptDraft.documentName,
      retakeDeadline,
      recordedAt: new Date().toISOString(),
      recordedBy: canonicalizeEmail(actorEmail),
    };

    const input = {
      id: certification.id,
      result: attemptDraft.result,
      examDate: attemptDraft.examDate || undefined,
      resultDate: attemptDraft.resultDate,
      /* Explicitly null, not omitted: an omitted field leaves the old deadline in place, which
         is exactly the stale "retake required" this exists to remove. */
      retakeDeadline: retakeDeadline || null,
      completionDate: failed ? null : attemptDraft.resultDate,
      /* A retake re-dates the validity. Explicitly null on a failure so an expiry left from the
         earlier pass cannot linger on a record that now reads Failed. */
      expiryDate: failed ? null : attemptDraft.expiryDate || null,
      documentKey: attemptDraft.documentKey,
      documentName: attemptDraft.documentName,
      attempts: JSON.stringify([...(certification.attempts || []), attempt]),
    };

    const result: any = await getClient().graphql({
      query: updateMutation,
      variables: { input },
      ...authMode,
    });
    const row = getData(result)?.updateCertification;
    if (!row) throw new CertificationsNotDeployedError();
    return fromRow(row);
  }

  /**
   * Record the Certification Admin's decision — the third gate.
   *
   * Writes ONLY to the certification. The expense's own status is the shared workflow's to
   * change, and this stage sits inside MANAGER_APPROVED rather than replacing it: approving here
   * releases the claim to Finance, it does not approve the claim.
   *
   * A rejection is a different matter. There is no "certification admin rejected" expense status
   * to move to, and leaving the claim at MANAGER_APPROVED would put a declined application in
   * Finance's queue as though nothing had happened — so the caller rejects the expense as well,
   * through the ordinary rejection path. See CertificationApprovals.
   */
  static async recordCertAdminDecision(
    certificationId: string,
    decision: CertAdminDecision,
    actorEmail: string,
    note?: string
  ): Promise<Certification> {
    const result: any = await getClient().graphql({
      query: updateMutation,
      variables: {
        input: {
          id: certificationId,
          certAdminDecision: decision,
          certAdminBy: canonicalizeEmail(actorEmail),
          certAdminAt: new Date().toISOString(),
          certAdminNote: note || null,
        },
      },
      ...authMode,
    });
    const row = getData(result)?.updateCertification;
    if (!row) throw new CertificationsNotDeployedError();
    return fromRow(row);
  }

  /** Link a certification to the reimbursement application raised for it. */
  static async attachToExpense(certificationId: string, expenseId: string): Promise<void> {
    await getClient().graphql({
      query: updateMutation,
      variables: { input: { id: certificationId, expenseId } },
      ...authMode,
    });
  }
}

/**
 * Filter a list the way the search bar does.
 *
 * Extracted from the page so the matching rule is testable without rendering: it decides what an
 * employee can find, and "search returns nothing" is indistinguishable from "there is nothing"
 * on screen.
 *
 * Employee name and id are matched only when `includePeople` — a viewer who cannot see other
 * people's certifications must not be able to confirm one exists by searching for its owner.
 */
export function filterCertifications(
  certifications: Certification[],
  term: string,
  options: { includePeople?: boolean } = {}
): Certification[] {
  const needle = String(term || '').trim().toLowerCase();
  if (!needle) return certifications;

  return certifications.filter((c) => {
    const fields = [c.name, c.provider, c.result];
    if (options.includePeople) fields.push(c.employeeName || '', c.employeeEmail, c.employeeId || '');
    return fields.some((field) => String(field || '').toLowerCase().includes(needle));
  });
}
